import { SHIPS, type ShipColor, type ShipSpec } from '../ship/ShipCatalog';

export type MenuChoice = { name: string; shipId: string; color: ShipColor };

const COLOR_HEX: Record<ShipColor, string> = {
  blue: '#3f7bff', green: '#3fcf6f', orange: '#ff9a3c', purple: '#a86bff', red: '#ff4b4b',
};

function sanitizeName(s: string): string {
  return s.replace(/[^\p{L}\p{N} _.\-]/gu, '').trim().slice(0, 16);
}

/**
 * Main menu: pilot name, ship carousel (the 3D preview renders in the game scene behind it),
 * colour swatches, stats and Launch. Keyboard: ←/→ ship, ↑/↓ colour, Enter launch.
 */
export class Menu {
  readonly root: HTMLDivElement;
  private index = 0;
  private color: ShipColor = 'blue';
  private readonly nameInput: HTMLInputElement;
  private readonly shipName: HTMLElement;
  private readonly shipRole: HTMLElement;
  private readonly stats: HTMLElement;
  private readonly swatches: HTMLElement;
  private readonly launch: HTMLButtonElement;
  private readonly online: HTMLElement;
  onChange: (shipId: string, color: ShipColor) => void = () => {};
  onLaunch: (c: MenuChoice) => void = () => {};
  onSound: (k: 'hover' | 'click' | 'confirm') => void = () => {};

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'menu';
    this.root.innerHTML = `
      <header class="menu-brand">
        <div class="menu-title">THE SPACESHIP</div>
        <div class="menu-tag">The real solar system at 1:1 scale · fly it together</div>
      </header>
      <section class="menu-panel">
        <label class="menu-field">
          <span>PILOT NAME</span>
          <input id="pilot-name" maxlength="16" autocomplete="off" spellcheck="false" placeholder="Your name" />
        </label>
        <div class="menu-ship">
          <button class="menu-arrow" data-dir="-1" aria-label="Previous ship">‹</button>
          <div class="menu-ship-info">
            <div class="menu-ship-name"></div>
            <div class="menu-ship-role"></div>
          </div>
          <button class="menu-arrow" data-dir="1" aria-label="Next ship">›</button>
        </div>
        <div class="menu-stats"></div>
        <div class="menu-swatches" role="radiogroup" aria-label="Hull colour"></div>
        <button class="menu-launch">LAUNCH</button>
        <div class="menu-online"></div>
      </section>
      <footer class="menu-help">
        <span>W/S throttle</span><span>Mouse steer</span><span>A/D roll</span><span>R/F up/down</span><span>H warp</span>
        <span>Click/Space fire</span><span>T target</span><span>J autopilot</span><span>M map</span><span>C camera</span><span>Tab players</span><span>Enter chat</span>
      </footer>`;
    parent.appendChild(this.root);
    this.nameInput = this.root.querySelector('#pilot-name')!;
    this.shipName = this.root.querySelector('.menu-ship-name')!;
    this.shipRole = this.root.querySelector('.menu-ship-role')!;
    this.stats = this.root.querySelector('.menu-stats')!;
    this.swatches = this.root.querySelector('.menu-swatches')!;
    this.launch = this.root.querySelector('.menu-launch')!;
    this.online = this.root.querySelector('.menu-online')!;

    try {
      const saved = JSON.parse(localStorage.getItem('spaceship.pilot') ?? 'null');
      if (saved) {
        this.nameInput.value = saved.name ?? '';
        this.index = Math.max(0, SHIPS.findIndex((s) => s.id === saved.shipId));
        this.color = saved.color ?? 'blue';
      }
    } catch {
      /* ignore */
    }

    this.root.querySelectorAll<HTMLButtonElement>('.menu-arrow').forEach((b) => {
      b.addEventListener('click', () => this.step(Number(b.dataset.dir)));
      b.addEventListener('mouseenter', () => this.onSound('hover'));
    });
    this.launch.addEventListener('click', () => this.submit());
    this.launch.addEventListener('mouseenter', () => this.onSound('hover'));
    this.nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.submit();
      e.stopPropagation();
    });
    window.addEventListener('keydown', this.onKey);
    this.render();
  }

  private readonly onKey = (e: KeyboardEvent) => {
    if (this.root.classList.contains('hidden') || document.activeElement === this.nameInput) return;
    if (e.key === 'ArrowLeft') this.step(-1);
    if (e.key === 'ArrowRight') this.step(1);
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') this.stepColor(e.key === 'ArrowUp' ? -1 : 1);
    if (e.key === 'Enter') this.submit();
  };

  /** Re-emit the current choice (the first render happens before handlers are attached). */
  emitChange(): void {
    this.onChange(this.spec.id, this.color);
  }

  get spec(): ShipSpec {
    return SHIPS[this.index];
  }

  setOnline(n: number | null): void {
    this.online.textContent = n === null ? 'Connecting to the shared universe…' : `${n} pilot${n === 1 ? '' : 's'} online`;
  }

  show(): void {
    this.root.classList.remove('hidden');
    document.exitPointerLock?.();
    setTimeout(() => (this.nameInput.value ? this.launch : this.nameInput).focus(), 50);
  }

  hide(): void {
    this.root.classList.add('hidden');
    // The name field keeps focus after Enter; release it so flight keys reach the game.
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  private step(dir: number): void {
    this.index = (this.index + dir + SHIPS.length) % SHIPS.length;
    if (!this.spec.colors.includes(this.color)) this.color = this.spec.colors[0];
    this.onSound('click');
    this.render();
  }

  private stepColor(dir: number): void {
    const cs = this.spec.colors;
    this.color = cs[(cs.indexOf(this.color) + dir + cs.length) % cs.length];
    this.onSound('click');
    this.render();
  }

  private render(): void {
    const s = this.spec;
    if (!s.colors.includes(this.color)) this.color = s.colors[0];
    this.shipName.textContent = s.name;
    this.shipRole.textContent = `${s.role} · ${s.length} m`;
    const bar = (label: string, v: number) =>
      `<div class="stat"><span>${label}</span><div class="stat-bar"><i style="transform:scaleX(${v})"></i></div></div>`;
    this.stats.innerHTML = bar('SPEED', s.speed) + bar('HANDLING', s.handling) + bar('SHIELD', s.shield);
    this.swatches.innerHTML = '';
    for (const c of s.colors) {
      const b = document.createElement('button');
      b.className = 'swatch' + (c === this.color ? ' on' : '');
      b.style.setProperty('--sw', COLOR_HEX[c]);
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', String(c === this.color));
      b.setAttribute('aria-label', c);
      b.addEventListener('click', () => {
        this.color = c;
        this.onSound('click');
        this.render();
      });
      this.swatches.appendChild(b);
    }
    this.onChange(s.id, this.color);
  }

  private submit(): void {
    const name = sanitizeName(this.nameInput.value) || `Pilot-${Math.floor(Math.random() * 900 + 100)}`;
    this.nameInput.value = name;
    const choice = { name, shipId: this.spec.id, color: this.color };
    try {
      localStorage.setItem('spaceship.pilot', JSON.stringify(choice));
    } catch {
      /* ignore */
    }
    this.onSound('confirm');
    this.onLaunch(choice);
  }
}
